import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader, Search, ChevronUp, ChevronDown, ChevronsUp, X, Plus, ArrowRight, GripVertical, RefreshCw } from 'lucide-react';
import { getAccessToken } from '../services/auth';

// ORDER MY DAY - the master list, and his own running order on top of it.
//
// Greg, 2026-09-11, the morning after the frog shipped: "I can't trust Claude right now to build
// my day for me... how do I order it so that it comes off in the way that I want it? I need, like,
// a place to see the master list and be able to change it around so that, okay, my day looks
// great. Give me the first frog, and let me go."
//
// ⚠️ HIS ORDER ALWAYS WINS, AND THE RANKING ONLY FILLS IN BEHIND IT. A ranking he did not set is
// a suggestion, not a plan. Anything he places by hand sorts above everything the scorer
// produced, and the score never gets to reorder his list. That is also what makes leaving the
// ranking switched on safe rather than presumptuous: it is the default for the ~170 items he has
// not thought about, never a claim about the ten he has.
//
// ⚠️ THE ORDER IS PARTIAL AND STANDING, NOT A DAILY RITUAL. He orders the handful he cares about
// and the rest stay ranked below; and it is not date-scoped, so a day he never opens this screen
// still starts from his last ordering. Requiring a complete daily re-triage would rebuild exactly
// the chore that killed the board.
//
// ⚠️ EVERY CHANGE SAVES ITSELF. There is no Save button to forget, because the failure this whole
// feature exists to fix is work that gets done and never recorded.

const PANEL = { background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10, display: 'flex', flexDirection: 'column', minHeight: 0 };
const HEAD = { padding: '12px 14px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 };
const BTN = (bg, border, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6,
  fontSize: 12, fontWeight: 600, background: bg, border: '1px solid ' + border, color,
  cursor: 'pointer', fontFamily: 'inherit',
});
const ICON_BTN = {
  background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex',
  alignItems: 'center', color: 'var(--gray-500)', borderRadius: 4,
};

const keyOf = i => i.srcTable + ' ' + i.srcId;

async function callFrog(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(base + '/frog', { method: 'POST', headers, body: JSON.stringify({ action, ...params }) });
  if (res.status === 403) { const e = new Error('not yours'); e.notMine = true; throw e; }
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'frog ' + res.status);
  return res.json();
}

export default function DayPlanner({ navigateTo }) {
  const [mine, setMine] = useState([]);
  const [rest, setRest] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [board, setBoard] = useState('all');
  const dragFrom = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await callFrog('plan');
      setMine(r.mine || []);
      setRest(r.rest || []);
      setErr(null);
    } catch (e) { setErr(e.notMine ? 'This board is not yours.' : e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Optimistic: the list moves under his hand and the write follows. A reorder that waits on a
  // round trip feels broken, and re-rendering from the server response would fight the drag.
  const persist = useCallback(async (next) => {
    setMine(next);
    setSaving(true);
    try {
      await callFrog('order_set', { keys: next.map(i => ({ srcTable: i.srcTable, srcId: i.srcId })) });
      setErr(null);
    } catch (e) { setErr('Could not save that order: ' + e.message); }
    finally { setSaving(false); }
  }, []);

  const move = (idx, to) => {
    if (to < 0 || to >= mine.length) return;
    const next = mine.slice();
    const [row] = next.splice(idx, 1);
    next.splice(to, 0, row);
    persist(next);
  };

  const addToDay = (item, top) => {
    const next = top ? [item, ...mine] : [...mine, item];
    setRest(r => r.filter(x => keyOf(x) !== keyOf(item)));
    persist(next);
  };

  const removeFromDay = (item) => {
    // Back into the ranked pool, not off the board. "I am not choosing this one" is a different
    // statement from "not today", and neither one closes anything.
    setRest(r => [...r, item].sort((a, b) => b.score - a.score));
    persist(mine.filter(x => keyOf(x) !== keyOf(item)));
  };

  const boards = useMemo(() => {
    const s = new Set([...mine, ...rest].map(i => i.domain).filter(Boolean));
    return ['all', ...Array.from(s).sort()];
  }, [mine, rest]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rest.filter(i => {
      if (board !== 'all' && i.domain !== board) return false;
      if (!needle) return true;
      return (i.title + ' ' + (i.reason || '')).toLowerCase().includes(needle);
    });
  }, [rest, q, board]);

  if (loading) {
    return <div style={{ padding: 24, color: 'var(--gray-500)' }}><Loader size={14} className="spin" /> Loading your board...</div>;
  }

  return (
    <div style={{ padding: 20, maxWidth: 1500 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Order my day</h1>
        {saving && <span style={{ fontSize: 11, color: 'var(--gray-500)' }}><Loader size={11} className="spin" /> saving</span>}
        <div style={{ flex: 1 }} />
        <button onClick={load} style={BTN('#fff', 'var(--gray-300)', 'var(--gray-700)')}>
          <RefreshCw size={12} /> Refresh
        </button>
        {mine.length > 0 && (
          <button onClick={() => navigateTo && navigateTo('todo-greg')}
            style={{ ...BTN('var(--orange)', 'var(--orange)', '#fff'), padding: '9px 18px', fontSize: 14, fontWeight: 700 }}>
            Start with #1 <ArrowRight size={15} />
          </button>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--gray-600)', marginBottom: 16, lineHeight: 1.5 }}>
        Put things in the order you want them on the left. <b>Number one becomes your frog.</b> You
        do not have to order everything &mdash; whatever you leave on the right stays ranked and
        falls in behind. It saves as you go and it stays put until you change it.
      </div>

      {err && (
        <div style={{ background: '#fff5f5', border: '1px solid #fbb', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#a12020', marginBottom: 12 }}>
          {err}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16, alignItems: 'start' }}>

        {/* ── My day ───────────────────────────────────────────────────────────────────── */}
        <div style={{ ...PANEL, border: '2px solid var(--orange)' }}>
          <div style={{ ...HEAD, borderBottom: '1px solid #f0c9b4' }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--orange)', letterSpacing: 0.5 }}>MY DAY</span>
            <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{mine.length} in order</span>
          </div>
          <div style={{ padding: 10, maxHeight: '62vh', overflowY: 'auto' }}>
            {mine.length === 0 ? (
              <div style={{ padding: '26px 14px', textAlign: 'center', color: 'var(--gray-500)', fontSize: 13, lineHeight: 1.6 }}>
                Nothing placed yet.<br />
                Add from the right, or press <b>Top</b> on the one thing you want to do first.
              </div>
            ) : mine.map((item, idx) => (
              <div key={keyOf(item)}
                draggable
                onDragStart={() => { dragFrom.current = idx; }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); if (dragFrom.current !== null && dragFrom.current !== idx) move(dragFrom.current, idx); dragFrom.current = null; }}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 9px', marginBottom: 6,
                  borderRadius: 7, border: '1px solid ' + (idx === 0 ? 'var(--orange)' : 'var(--gray-200)'),
                  background: idx === 0 ? '#fff8f3' : '#fff',
                }}>
                <GripVertical size={13} color="var(--gray-300)" style={{ marginTop: 3, cursor: 'grab', flexShrink: 0 }} />
                <div style={{
                  width: 22, height: 22, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800,
                  background: idx === 0 ? 'var(--orange)' : 'var(--gray-100)',
                  color: idx === 0 ? '#fff' : 'var(--gray-600)',
                }}>{idx + 1}</div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: idx === 0 ? 700 : 500, color: 'var(--navy)', lineHeight: 1.35 }}>
                    {item.title}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 2 }}>
                    {idx === 0 && <b style={{ color: 'var(--orange)' }}>YOUR FROG &middot; </b>}
                    {item.domain}
                    {item.skippedToday && <span style={{ color: '#b45309' }}> &middot; you passed on this today</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                  <button style={ICON_BTN} title="To the top" onClick={() => move(idx, 0)} disabled={idx === 0}><ChevronsUp size={14} /></button>
                  <button style={ICON_BTN} title="Up" onClick={() => move(idx, idx - 1)} disabled={idx === 0}><ChevronUp size={14} /></button>
                  <button style={ICON_BTN} title="Down" onClick={() => move(idx, idx + 1)} disabled={idx === mine.length - 1}><ChevronDown size={14} /></button>
                  <button style={{ ...ICON_BTN, color: 'var(--gray-400)' }} title="Take out of my day (it stays on the board)" onClick={() => removeFromDay(item)}><X size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Everything else ──────────────────────────────────────────────────────────── */}
        <div style={PANEL}>
          <div style={HEAD}>
            <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--navy)', letterSpacing: 0.5 }}>EVERYTHING ELSE</span>
            <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{filtered.length} of {rest.length}</span>
          </div>
          <div style={{ padding: '9px 12px', borderBottom: '1px solid var(--gray-100)', display: 'flex', gap: 8, flexShrink: 0 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={13} color="var(--gray-400)" style={{ position: 'absolute', left: 8, top: 8 }} />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search everything..."
                style={{ width: '100%', padding: '6px 8px 6px 26px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>
            <select value={board} onChange={e => setBoard(e.target.value)}
              style={{ padding: '6px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', maxWidth: 190 }}>
              {boards.map(b => <option key={b} value={b}>{b === 'all' ? 'Every board' : b}</option>)}
            </select>
          </div>

          <div style={{ padding: 10, maxHeight: '56vh', overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '26px 14px', textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
                {rest.length === 0 ? 'Everything open is in your day.' : 'Nothing matches that.'}
              </div>
            ) : filtered.map(item => (
              <div key={keyOf(item)}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 8px', marginBottom: 5, borderRadius: 6, border: '1px solid var(--gray-100)', background: '#fff' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: 'var(--gray-900)', lineHeight: 1.35 }}>{item.title}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.domain} &middot; {item.reason}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button onClick={() => addToDay(item, true)} title="Do this first" style={BTN('#fff8f3', 'var(--orange)', 'var(--orange)')}>
                    <ChevronsUp size={12} /> Top
                  </button>
                  <button onClick={() => addToDay(item, false)} title="Add to the end of my day" style={BTN('#fff', 'var(--gray-300)', 'var(--navy)')}>
                    <Plus size={12} /> Add
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
